<script setup>
import { computed, inject, onMounted, ref } from 'vue'
import { useAuthStore } from '../stores/AuthStore'
import {
  buildFooterLinks,
  distributeFooterLinksAcrossColumns
} from '../access/navigationLinks'
import {
  getDatabase,
  ref as dbRef,
  get as dbGet,
} from 'firebase/database'
import { getApp } from 'firebase/app'
import { handleErrors } from '../utils/errorHandler'

const props = defineProps({
  showMarginTop: {
    type: Boolean,
    default: true
  }
})

const db = getDatabase(getApp())
const socialMedia = ref([])

onMounted(async () => {
  await getSocialMedia()
})

const getSocialMedia = async () => {
  try {
    const socialMediaRef = dbRef(db, 'SOCIAL_MEDIA')
    const snapshot = await dbGet(socialMediaRef)
    if (snapshot.exists()) {
      socialMedia.value = snapshot.val()
    }
  } catch (err) {
    handleErrors('Error fetching social media:', err)
  }
}

const emitter = inject('emitter')
const customNavigation = inject('customNavigation')
const authStore = useAuthStore()

function navigationApi() {
  return customNavigation?.value ?? customNavigation
}

const footerLinkColumns = computed(() => {
  const routes = navigationApi()?.routes
  if (!routes) return [[]]
  return distributeFooterLinksAcrossColumns(buildFooterLinks(routes), authStore)
})

function footerColumnClass(columnIndex) {
  return columnIndex === 0 ? 'col-1 me-5 p-0' : 'col-2 me-3 p-0'
}

function navigateToLink(link) {
  if (!link?.routeName) return
  navigationApi().navigate({
    newPage: link.routeName,
    saveEvent: link.saveEvent === true
  })
}

function handleLinkAction(link) {
  if (link.action === 'contact') {
    emitter.emit('toggleContactModal')
  }
}
</script>

<template>
  <div :class="{ 'mt-4.5': props.showMarginTop }" class="footer-container bg-light border-top bcs-page-padding">
    <div class="container-fluid mx-0 px-3 pt-1">
      <footer class="row pt-4 pb-0 mb-0 text-14 text-primary">
        <div
          v-for="(columnLinks, columnIndex) in footerLinkColumns"
          :key="`footer-column-${columnIndex}`"
          :class="footerColumnClass(columnIndex)"
        >
          <ul class="nav flex-column footer-link-column">
            <li
              v-for="link in columnLinks"
              :key="`footer-col-${columnIndex}-${link.id}`"
              class="nav-item mb-2.5"
            >
              <a
                v-if="link.action !== 'privacy'"
                class="hand-cursor nav-link p-0"
                href="#"
                @click.prevent="link.action ? handleLinkAction(link) : navigateToLink(link)"
              >
                {{ link.label }}
              </a>
              <a
                v-else
                class="hand-cursor nav-link p-0"
                href="#"
                data-bs-toggle="modal"
                data-bs-target="#pendingFunctionModal"
                @click.prevent
              >
                {{ link.label }}
              </a>
            </li>
          </ul>
        </div>

        <div class="col ps-5"></div>

        <div class="col-md-6 text-end pe-0">
          <ul class="d-flex float-end list-unstyled">
            <li v-for="(social, platform) in socialMedia" :key="platform" class="ms-3">
              <a class="hand-cursor" target="_blank" :href="social.url" :aria-label="social.name">
                <i :class="[
                  platform === 'YouTube' ? 'fs-3' : 'fs-4',
                  'bi',
                  social.icon,
                  'social-icon'
                ]"></i>
              </a>
            </li>
          </ul>
        </div>
      </footer>
      <div class="row pb-2 pt-4">
        <div class="col-12 px-0">
          <i class="bi bi-c-circle"></i>
          <p class="ps-1 text-12 d-inline">
            {{ new Date().getFullYear() }} Benefits Compliance Solutions. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
